import { useQuery } from "@tanstack/react-query";
import type { Itinerary } from "@/types/dto/pdp/pdp-itinerary";
import { itineraryFactory } from "@/factories/pdp";

/**
 * Fetches itinerary data from the API route
 *
 * @param packageId - Package identifier
 * @param packageDateId - Package date identifier
 * @param neoId - Neo identifier
 * @param landingPageId - Landing page identifier
 * @returns Promise resolving to transformed Itinerary data
 */
export const fetchItineraryData = (
  packageId: string,
  packageDateId: string,
  neoId: string,
  landingPageId: string,
  currencyId: string,
  marketId: string
) => async (): Promise<Itinerary> => {
  try {
    const url = `/api/pdp/itinerary?landingPageId=${landingPageId}&packageId=${packageId}&packageDateId=${packageDateId}&neoId=${neoId}&currencyId=${currencyId}&marketId=${marketId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const itineraryData = await response.json();
    return itineraryFactory(itineraryData);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching itinerary data:", error); // TODO: log error to error tracking service
    throw error;
  }
};

/**
 * React Query hook for fetching itinerary data
 *
 * Follows established PDP patterns for data fetching via API routes.
 * Data is cached with React Query and never goes stale (staleTime: Infinity).
 *
 * @param packageId - Package identifier
 * @param packageDateId - Package date identifier
 * @param neoId - Neo identifier
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with data, isLoading, error states
 *
 * @example
 * ```tsx
 * const { data: itineraryData, isLoading } = useItinerary(
 *   "16128",
 *   "107496",
 *   "686"
 * );
 * ```
 */
const useItinerary = (
  packageId: string,
  packageDateId: string,
  neoId: string,
  landingPageId: string,
  currencyId: string,
  marketId: string,
  enabled: boolean = true
) => useQuery<Itinerary>({
  queryKey: ["PDP_ITINERARY", neoId, packageId, packageDateId, landingPageId, currencyId, marketId],
  queryFn: fetchItineraryData(packageId, packageDateId, neoId, landingPageId, currencyId, marketId),
  enabled: enabled && !!currencyId && !!marketId,
  staleTime: Infinity
});

export default useItinerary;
